import { Component } from '@theme/component';
import { removeTrapFocus } from '@theme/focus';

/**
 * OVID mobile header drawer.
 *
 * Manages the native `<dialog>` element and the L1 → L2 slide state machine.
 * Declarative handlers (`on:click="/close"`, `on:click="/openSubmenu/wines"`,
 * `on:click="/back"`) are wired through the project's base Component class.
 *
 * Public events:
 * - Listens for `drawer:toggle` dispatched from the header-menu trigger.
 * - Dispatches `menu:open` / `menu:close` (bubbling) so the header overlay
 *   can fade in and the body can scroll-lock.
 *
 * Accessibility:
 * - Uses `<dialog>.showModal()` for proper ARIA/focus semantics.
 * - Falls back to a focus trap + scroll lock for browsers that don't support
 *   native modal dialogs.
 * - Escape closes the L2 pane first, then the drawer itself.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} viewport
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['dialog', 'viewport'];

  /** @type {string | null} */
  #activePane = null;

  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  /**
   * Tracks the input modality of the most recent interaction so `open()` can
   * decide whether to move focus (keyboard) or stay quiet (pointer). 'keyboard'
   * is the default and is restored on any keydown; pointerdown overrides it.
   * @type {'keyboard' | 'mouse' | 'touch' | 'pen'}
   */
  #lastModality = 'keyboard';

  #onPointerDown = (/** @type {PointerEvent} */ e) => {
    this.#lastModality = /** @type {'mouse' | 'touch' | 'pen'} */ (e.pointerType || 'mouse');
  };

  #onKeyDown = () => { this.#lastModality = 'keyboard'; };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('pointerdown', this.#onPointerDown, { capture: true });
    document.addEventListener('keydown', this.#onKeyDown, { capture: true });
    this.addEventListener('drawer:toggle', this.#onToggleEvent);
    this.refs.dialog.addEventListener('cancel', this.#onDialogCancel);
    this.refs.dialog.addEventListener('close', this.#onDialogClose);
    this.refs.dialog.addEventListener('keydown', this.#onDialogKeydown);
    this.refs.dialog.addEventListener('click', this.#onDialogClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.#onPointerDown, { capture: true });
    document.removeEventListener('keydown', this.#onKeyDown, { capture: true });
    this.removeEventListener('drawer:toggle', this.#onToggleEvent);
    this.refs.dialog.removeEventListener('cancel', this.#onDialogCancel);
    this.refs.dialog.removeEventListener('close', this.#onDialogClose);
    this.refs.dialog.removeEventListener('keydown', this.#onDialogKeydown);
    this.refs.dialog.removeEventListener('click', this.#onDialogClick);
    removeTrapFocus();
  }

  /* --------------------------------------------------------------------
   * Declarative handlers (via `on:click`)
   * -------------------------------------------------------------------- */

  /**
   * Toggle open/close. Called by the hamburger via `drawer:toggle`, and used
   * directly from declarative bindings if desired.
   * @param {Event} [event]
   */
  toggle = (event) => {
    if (event?.target instanceof HTMLElement) {
      this.#lastTrigger = event.target.closest('button, a') ?? event.target;
    }
    if (this.refs.dialog.open) {
      this.close();
    } else {
      this.open();
    }
  };

  open = () => {
    if (this.refs.dialog.open) return;

    // Use non-modal `show()` rather than `showModal()` so the main header
    // (and in particular its MENU/CLOSE trigger) stays interactive while
    // the drawer is open. We handle focus management ourselves.
    if (typeof this.refs.dialog.show === 'function') {
      try {
        this.refs.dialog.show();
      } catch {
        this.refs.dialog.setAttribute('open', '');
      }
    } else {
      this.refs.dialog.setAttribute('open', '');
    }

    document.documentElement.style.setProperty('overflow', 'hidden');
    this.dispatchEvent(new CustomEvent('menu:open', { bubbles: true }));
    this.#resetToL1();

    // Record how the drawer was opened. WebKit runs the dialog focusing steps
    // asynchronously after show() and focuses the first link, which paints a
    // focus ring on touch/mouse opens. We can't reliably out-race that
    // focus, so the visible ring is suppressed via CSS keyed on this attribute
    // (see header-drawer.liquid); only keyboard opens keep the ring.
    const viaKeyboard = this.#lastModality === 'keyboard';
    this.refs.dialog.dataset.openedByKeyboard = String(viaKeyboard);

    if (viaKeyboard) {
      requestAnimationFrame(() => {
        const first = this.querySelector(
          '[data-pane="l1"] .header-drawer__link, [data-pane="l1"] a, [data-pane="l1"] button'
        );
        if (first instanceof HTMLElement) first.focus();
      });
    }
  };

  close = () => {
    if (!this.refs.dialog.open) return;

    this.refs.dialog.classList.add('is-closing');
    const onEnd = () => {
      this.refs.dialog.classList.remove('is-closing');
      this.refs.dialog.removeEventListener('animationend', onEnd);
      if (typeof this.refs.dialog.close === 'function') {
        try {
          this.refs.dialog.close();
        } catch {
          this.refs.dialog.removeAttribute('open');
        }
      } else {
        this.refs.dialog.removeAttribute('open');
      }
    };
    this.refs.dialog.addEventListener('animationend', onEnd);

    // Fallback if no animation runs.
    window.setTimeout(() => onEnd(), 350);
  };

  /**
   * Opens the L2 pane for the given parent link handle.
   * Bound via `on:click="/openSubmenu/<handle>"`.
   * @param {string} handle
   */
  openSubmenu = (handle) => {
    const pane = this.querySelector(`[data-pane="l2"][data-parent="${cssEscape(String(handle))}"]`);
    if (!(pane instanceof HTMLElement)) return;

    // Deactivate any previous L2 pane.
    this.querySelectorAll('[data-pane="l2"]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.dataset.active = 'false';
        el.hidden = true;
      }
    });

    pane.hidden = false;
    // Force a reflow so the transition from translateX(100%) → 0 actually runs.
    void pane.offsetWidth;
    pane.dataset.active = 'true';
    this.#activePane = String(handle);
    this.refs.viewport.dataset.level = '2';

    // Update aria-expanded on the trigger.
    const trigger = this.querySelector(
      `[aria-controls="header-drawer-pane-${cssEscape(String(handle))}"]`
    );
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Move focus to the back button of the opened pane.
    const back = pane.querySelector('.header-drawer__back');
    if (back instanceof HTMLElement) requestAnimationFrame(() => back.focus());
  };

  /**
   * Returns from L2 to L1.
   * Bound via `on:click="/back"`.
   */
  back = () => {
    const active = this.#activePane;
    this.refs.viewport.dataset.level = '1';

    this.querySelectorAll('[data-pane="l2"]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.dataset.active = 'false';
        // Defer hiding until after the transition so it can slide out.
        window.setTimeout(() => {
          if (el.dataset.active === 'false') el.hidden = true;
        }, 250);
      }
    });

    if (active) {
      const trigger = this.querySelector(
        `[aria-controls="header-drawer-pane-${cssEscape(active)}"]`
      );
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    }

    this.#activePane = null;
  };

  /* --------------------------------------------------------------------
   * Internal event handlers
   * -------------------------------------------------------------------- */

  #onToggleEvent = () => this.toggle();

  /** Native `cancel` (Escape key) on the dialog. */
  #onDialogCancel = (/** @type {Event} */ event) => {
    // If an L2 pane is open, swallow and go back first.
    if (this.#activePane) {
      event.preventDefault();
      this.back();
    }
  };

  #onDialogClose = () => {
    removeTrapFocus();
    document.documentElement.style.removeProperty('overflow');
    this.#resetToL1();
    this.dispatchEvent(new CustomEvent('menu:close', { bubbles: true }));
    this.#lastTrigger?.focus();
    this.#lastTrigger = null;
  };

  /**
   * Some browsers don't emit cancel consistently for keydown — mirror the
   * Escape behaviour here as a safeguard.
   * @param {KeyboardEvent} event
   */
  #onDialogKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#activePane) return;
    event.preventDefault();
    this.back();
  };

  /**
   * Clicking outside the shell closes the dialog (matches ::backdrop clicks).
   * @param {MouseEvent} event
   */
  #onDialogClick = (event) => {
    if (event.target !== this.refs.dialog) return;
    this.close();
  };

  #resetToL1() {
    this.refs.viewport.dataset.level = '1';
    this.querySelectorAll('[data-pane="l2"]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.dataset.active = 'false';
        el.hidden = true;
      }
    });
    this.querySelectorAll('[aria-controls^="header-drawer-pane-"]').forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
    });
    this.#activePane = null;
  }
}

/**
 * @param {string} str
 * @returns {string}
 */
function cssEscape(str) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(str);
  }
  return str.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
